import { v4 as uuid } from 'uuid';
import {
  Box, Grid, Link,
} from '@mui/material';
import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import EditableText from '../../Form/EditableText';
import CloseButton from '../../Buttons/Close';
import Utils from '../../../utils/Utils';
import BlockProps from '../../../models/BlockProps';
import BlockData from '../../../models/BlockData';
import LinkButton from '../../Buttons/LinkButton';
import styles from './BlockCard.module.css';
import BlockCardData, { BlockCardVariant } from '../../../models/Block/BlockCardData';
import useResponsive from '../../../hooks/UseResponsive';
import CroppedImage from '../../CroppedImage';
import ImageSettingsModal from '../../Modals/ImageSettingsModal/ImageSettingsModal';
import BlockComponentImage from '../../../models/BlockComponentValues/BlockComponentImage';
import useGetBackground from '../../../hooks/UseGetBackground';
import BackGroundImageModal from '../../Modals/ImageModal/BackGroundImageModal';
import useParallaxScript from '../../../hooks/useParallaxScript';
import config from '../../../config';
import ImageEnlargeModal from '../../Images/ImageEnlargeModal';

export interface BlockCardProps extends BlockProps {
  blockData: BlockData[]
}

function BlockCard({
  data,
  isEditing,
  isTemplate,
  blockData,
  editHandler,
  showImageModal,
  onCloseImageModal,
  mainColor,
}: BlockCardProps) {
  const cardItemId = uuid();
  const targetModalId = uuid();

  const [props, setProps] = useState<BlockCardData>(data.data as BlockCardData);

  useEffect(() => {
    setProps(data.data as BlockCardData);
  }, [data.data]);

  const deleteImage = useCallback(() => {
    props.mainImage = undefined;
    editHandler(data.id, props);
  }, [props, editHandler, data.id]);

  useResponsive({
    items: [
      {
        wrapperId: `card-wrapper-${cardItemId}`,
        targetId: `card-image-${cardItemId}`,
        queries: [
          { size: 0, className: styles.imageSideSmartphonePreview },
          { size: 600, className: styles.imageSide },
        ],
      },
      {
        wrapperId: `card-wrapper-${cardItemId}`,
        targetId: `card-header-${cardItemId}`,
        queries: [
          { size: 0, className: styles.headerSmartphonePreview },
          { size: 600, className: styles.header },
        ],
      },
      {
        wrapperId: `card-header-${cardItemId}`,
        targetId: `card-title-${cardItemId}`,
        queries: [
          { size: 0, className: styles.titleSmartphonePreview },
          { size: 600, className: styles.title },
        ],
      },

      props.variant === BlockCardVariant.Left
        ? {
          wrapperId: `card-wrapper-${cardItemId}`,
          targetId: `card-text-${cardItemId}`,
          queries: [
            { size: 0, className: styles.textSideLeftSmartphonePreview },
            { size: 600, className: styles.textSideLeft },
          ],
        }
        : {
          wrapperId: `card-wrapper-${cardItemId}`,
          targetId: `card-text-${cardItemId}`,
          queries: [
            { size: 0, className: styles.textSideRightSmartphonePreview },
            { size: 600, className: styles.textSideRight },
          ],
        },
      props.variant === BlockCardVariant.Right && {
        wrapperId: `card-wrapper-${cardItemId}`,
        targetId: `card-wrapper-${cardItemId}`,
        queries: [
          { size: 0, className: styles.containerSmartphonePreview },
          { size: 600, className: styles.container },
        ],
      },
    ],
  });

  const imageSide = useMemo(() => (
    <Grid
      id={`card-image-${cardItemId}`}
      item
      xs={6}
      minWidth={300}
    >

      {props.mainImage && (
        <Box sx={Utils.hoverBorder(isEditing)}>

          {isEditing && <CloseButton updateHandler={() => { deleteImage(); }} />}
          <ImageSettingsModal
            image={props.mainImage}
            isEditing={isEditing}
            disabled={isTemplate || !isEditing}
            blockData={blockData}
            onConfirm={(image: BlockComponentImage) => {
              props.mainImage = image;
              editHandler(data.id, props);
            }}
          >
            {(!isEditing) ? (
              <Link
                style={{ cursor: props?.mainImage?.link?.linkData?.url ? 'pointer' : 'default' }}
                target={props?.mainImage?.link?.linkData?.openType}
                href={props?.mainImage?.link?.linkData?.url || undefined}
                sx={{
                  width: '100%',
                  height: '100%',
                }}
                rel="noreferrer"
              >
                <CroppedImage
                  isEditing={isEditing}
                  image={props.mainImage}
                  targetModalId={targetModalId}
                  settings={props.mainImage.settings}
                />
              </Link>
            ) : (
              <CroppedImage
                isEditing={isEditing}
                image={props.mainImage}
                targetModalId={targetModalId}
                settings={props.mainImage.settings}
              />
            )}
          </ImageSettingsModal>
        </Box>
      )}

    </Grid>
  ), [cardItemId, data.id, deleteImage, editHandler, isEditing, isTemplate, props, blockData]);

  const textSide = useMemo(() => (
    <Grid
      id={`card-text-${cardItemId}`}
      item
      xs={6}
      minWidth={300}
      alignItems="start"
      justifyContent="start"
    >
      {props.descriptionHeader && (
        <Grid
          item
          xs="auto"
          sx={[Utils.hoverBorder(isEditing), { marginBottom: 2 }]}
        >

          {isEditing && (
            <CloseButton
              updateHandler={() => {
                props.descriptionHeader = undefined;
                editHandler(data.id, props);
              }}
            />
          )}

          <EditableText
            text={props.descriptionHeader.value}
            textColor={props.descriptionHeader.settings?.textColor}
            isEditable={isEditing && !isTemplate}
            updateHandler={(text) => {
              props.descriptionHeader.value = text;
              editHandler(data.id, props);
            }}
          />

        </Grid>
      )}
      {props.descriptionSubHeader && (
        <Grid
          item
          xs="auto"
          sx={[Utils.hoverBorder(isEditing), { marginBottom: 2 }]}
        >

          {isEditing && (
            <CloseButton
              updateHandler={() => {
                props.descriptionSubHeader = undefined;
                editHandler(data.id, props);
              }}
            />
          )}

          <EditableText
            text={props.descriptionSubHeader.value}
            textColor={props.descriptionSubHeader.settings?.textColor}
            isEditable={isEditing && !isTemplate}
            updateHandler={(text) => {
              props.descriptionSubHeader.value = text;
              editHandler(data.id, props);
            }}
          />

        </Grid>
      )}
      {props.descriptionBody && (
        <Grid
          item
          xs="auto"
          sx={[Utils.hoverBorder(isEditing), { marginBottom: 2 }]}
        >

          {isEditing && (
            <CloseButton
              updateHandler={() => {
                props.descriptionBody = undefined;
                editHandler(data.id, props);
              }}
            />
          )}

          <EditableText
            text={props.descriptionBody.value}
            textColor={props.descriptionBody.settings?.textColor}
            isEditable={isEditing && !isTemplate}
            updateHandler={(text) => {
              props.descriptionBody.value = text;
              editHandler(data.id, props);
            }}
          />

        </Grid>
      )}

      {props.button && (
        <Box sx={Utils.hoverBorder(isEditing)}>
          <CloseButton
            show={isEditing}
            updateHandler={() => {
              props.button = undefined;
              editHandler(data.id, props);
            }}
          />
          <LinkButton
            mainColor={mainColor}
            button={props.button}
            blockData={blockData}
            isEditable={isEditing && !isTemplate}
            isTemplate={isTemplate}
            updateHandler={(item) => {
              props.button = item;
              editHandler(data.id, props);
            }}
          />
        </Box>
      )}
    </Grid>
  ), [blockData, cardItemId, data.id, editHandler, isEditing, isTemplate, props]);

  const elementUuid = uuid();
  useParallaxScript(props.backgroundImage?.settings?.parallaxBool, elementUuid, config.backgroundImage.parallaxEffect);

  return (
    <>
      <Grid
        id={elementUuid}
        container
        spacing={0}
        direction="column"
        alignItems="center"
        justifyContent="center"
        sx={{
          minHeight: '250px',
          overflow: 'hidden',
          ...useGetBackground(
            props.backgroundImage?.solidColor,
            props.backgroundImage?.value,
            props.backgroundImage?.settings?.scale,
            props.backgroundImage?.settings?.scaleBool,
          ),
          padding: 2,
          backgroundPosition: 'center 0px',
        }}
      >
        <Grid
          id={`card-header-${cardItemId}`}
          container
          spacing={0}
          direction="column"
          justifyContent="end"
        >

          {(isEditing && !isTemplate) && (
            <BackGroundImageModal
              backgroundImage={props.backgroundImage}
              isShowModal={showImageModal}
              onCloseModal={onCloseImageModal}
              handleOnConfirm={(url: BlockComponentImage) => {
                const tempBg = { ...props, backgroundImage: { ...url } };
                setProps(tempBg);
                editHandler(data.id, tempBg);
              }}
            />
          )}

          <Grid item xs={12} sx={{ paddingTop: 1 }} />

          <Grid
            container
            spacing={0}
            direction="column"
            alignItems="center"
            justifyContent="center"
            className="block-content-wrapper"
          >
            {props.title && (
              <Grid
                id={`card-title-${cardItemId}`}
                item
                xs={12}
                alignContent="start"
                justifyContent="start"
                sx={{
                  paddingTop: 2,
                  maxHeight: '100%',
                  height: '100%',
                }}
              >
                <Box sx={Utils.hoverBorder(isEditing)}>

                  {isEditing && (
                    <CloseButton
                      updateHandler={() => {
                        props.title = undefined;
                        editHandler(data.id, props);
                      }}
                    />
                  )}

                  <EditableText
                    text={props.title.value}
                    textColor={props.title.settings?.textColor}
                    isEditable={isEditing && !isTemplate}
                    updateHandler={(text) => {
                      props.title.value = text;
                      editHandler(data.id, props);
                    }}
                  />
                </Box>
              </Grid>
            )}

            <Grid
              id={`card-wrapper-${cardItemId}`}
              container
              spacing={0}
              direction="row"
              alignItems="stretch"
              justifyContent="center"
              sx={{
                paddingTop: 2,
              }}
            >
              {props.variant === BlockCardVariant.Left ? (
                <>
                  {imageSide}
                  {textSide}
                </>
              ) : (
                <>
                  {textSide}
                  {imageSide}
                </>
              )}
            </Grid>
          </Grid>
        </Grid>

      </Grid>

      <ImageEnlargeModal
        images={[props?.mainImage]}
        targetModalId={targetModalId}
      />
    </>
  );
}

export default BlockCard;
